import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LayersIcon from '@mui/icons-material/Layers';
import ShieldIcon from '@mui/icons-material/Shield';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { colors, status } from '../../theme';

const FLOW_ACCENTS = {
  'morning-prep': { icon: FlashOnIcon, color: status.info.text, bg: status.info.bg, border: status.info.border },
  'market-close-core': { icon: LayersIcon, color: status.purple.text, bg: status.purple.bg, border: status.purple.border },
  'post-close-alpha': { icon: FlashOnIcon, color: status.warning.text, bg: status.warning.bg, border: status.warning.border },
  'hedge-recovery': { icon: ShieldIcon, color: status.negative.text, bg: status.negative.bg, border: status.negative.border },
  'scanner-intelligence': { icon: CompareArrowsIcon, color: status.positive.text, bg: status.positive.bg, border: status.positive.border },
};

function AutomationFlowCard({ flow, onTrigger, isTriggering, t }) {
  const accent = FLOW_ACCENTS[flow.key] || { icon: LayersIcon, color: colors.gray[700], bg: colors.gray[100], border: colors.gray[200] };
  const AccentIcon = accent.icon;
  const isActive = flow.active_job_count > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        borderColor: colors.gray[200],
        boxShadow: 'none',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)',
          borderColor: accent.color,
        },
      }}
    >
      {/* Accent top border */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          bgcolor: accent.color,
        }}
      />

      <CardContent
        sx={{
          flex: 1,
          p: 1.5,
          pt: 1.75,
          '&:last-child': { pb: 1.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Header: icon + title + state chip */}
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: accent.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AccentIcon sx={{ fontSize: 18, color: accent.color }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mb: 0.35 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: 14.5,
                  lineHeight: 1.25,
                  color: colors.gray[800],
                  flex: 1,
                  minWidth: 0,
                  wordBreak: 'break-word',
                }}
              >
                {flow.title}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ flexShrink: 0, mt: 0.35 }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: isActive ? status.positive.main : colors.gray[300],
                    boxShadow: isActive ? `0 0 0 3px ${status.positive.bg}` : 'none',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: isActive ? status.positive.text : colors.gray[500],
                    letterSpacing: 0.2,
                  }}
                >
                  {isActive ? t('automation.flow.active', 'Running') : t('automation.flow.ready', 'Ready')}
                </Typography>
              </Stack>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: 12.5,
                lineHeight: 1.4,
              }}
            >
              {flow.summary}
            </Typography>
          </Box>
        </Stack>

        {/* Steps list */}
        <Box sx={{ pt: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                color: colors.gray[500],
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {t('automation.flow.steps', 'Steps')}
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.gray[700] }}>
              {flow.enabled_job_count}
              <Typography component="span" sx={{ fontSize: 11, color: colors.gray[400], fontWeight: 500 }}>
                /{flow.job_count}
              </Typography>
            </Typography>
            {isActive && (
              <Typography sx={{ fontSize: 10.5, color: status.positive.text, fontWeight: 700 }}>
                · {flow.active_job_count} {t('automation.flow.running', 'running')}
              </Typography>
            )}
          </Stack>
          <Stack spacing={0.3}>
            {flow.jobs.map((job, idx) => (
              <Tooltip key={job.key} title={job.schedule_label} arrow placement="left">
                <Stack
                  direction="row"
                  spacing={0.85}
                  alignItems="baseline"
                  sx={{ cursor: 'help', '&:hover .step-title': { color: accent.color } }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: colors.gray[400],
                      width: 14,
                      flexShrink: 0,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {idx + 1}.
                  </Typography>
                  <Typography
                    className="step-title"
                    sx={{
                      fontSize: 12.5,
                      lineHeight: 1.45,
                      color: colors.gray[700],
                      transition: 'color 0.15s ease',
                    }}
                  >
                    {job.title}
                  </Typography>
                </Stack>
              </Tooltip>
            ))}
          </Stack>
        </Box>

        {/* Trigger button — pinned to bottom */}
        <Box sx={{ mt: 'auto', pt: 0.75 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={isTriggering
              ? <CircularProgress size={14} thickness={5} sx={{ color: accent.color }} />
              : <RocketLaunchIcon sx={{ fontSize: 16 }} />}
            onClick={() => onTrigger(flow.key)}
            disabled={isTriggering}
            fullWidth
            sx={{
              borderRadius: 1.5,
              boxShadow: 'none',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 12.5,
              py: 0.65,
              color: accent.color,
              borderColor: colors.gray[200],
              bgcolor: '#fff',
              '&:hover': {
                bgcolor: accent.bg,
                borderColor: accent.color,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                color: accent.color,
                borderColor: accent.border,
                bgcolor: accent.bg,
                opacity: 0.9,
              },
            }}
          >
            {isTriggering ? t('automation.flow.triggering', 'Queueing…') : t('automation.flow.trigger', 'Trigger Flow')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

AutomationFlowCard.propTypes = {
  flow: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    job_keys: PropTypes.arrayOf(PropTypes.string).isRequired,
    job_count: PropTypes.number.isRequired,
    enabled_job_count: PropTypes.number.isRequired,
    active_job_count: PropTypes.number.isRequired,
    jobs: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        schedule_label: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  onTrigger: PropTypes.func.isRequired,
  isTriggering: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

export default AutomationFlowCard;